import { create } from 'zustand';
import type { Domain, OutcomeGroup } from '../types/Domains';
import type { Outcome, BetFormat } from '../types/Outcome';
import type { BettingStrategy } from '../types/BettingStrategy';
import type { ApiServices } from '../hooks/useApi';
import type { Theme } from '@mui/material/styles';
import { useUserStore } from './userStore';
import { getMultiplierFromBetFormat, getBetFormatWithFallback } from '../utils/pickHelpers';

export type ThemeMode = 'light' | 'dark' | 'system';

interface UIState {
    themeMode: ThemeMode;
    sidebar: {
        isCondensed: boolean;
        isMobile: boolean;
        isOpen: boolean;
    };
    refreshing: boolean;
    progressMessage: string | null;
    appRefreshing: boolean;
    pageRefreshing: boolean;
    suppressBusMessages: boolean;
    showMemoryMonitor: boolean;
    showSocketDebug: boolean;
    forceEnableModelActions: boolean;
    settingsDialog: {
        open: boolean;
        initialTab: number;
    };
}

interface ReferenceData {
    domains: Domain[];
    bettingStrategies: BettingStrategy[];
    isLoading: boolean;
}

interface AppState {
    // UI State
    ui: UIState;
    // Reference Data
    referenceData: ReferenceData;
    // Actions
    setIsCondensed: (value: boolean) => void;
    setIsOpen: (value: boolean) => void;
    setRefreshing: (value: boolean) => void;
    setProgressMessage: (message: string | null) => void;
    setAppRefreshing: (value: boolean) => void;
    setPageRefreshing: (value: boolean) => void;
    setIsMobile: (value: boolean) => void;
    setSuppressBusMessages: (value: boolean) => void;
    setShowMemoryMonitor: (value: boolean) => void;
    setShowSocketDebug: (value: boolean) => void;
    setForceEnableModelActions: (value: boolean) => void;
    setThemeMode: (mode: ThemeMode) => void;
    openSettingsDialog: (tab?: number) => void;
    closeSettingsDialog: () => void;
    refreshReferenceData: (api?: ApiServices) => Promise<void>;
    loadReferenceData: (api: ApiServices) => Promise<void>;
    // Reference data helpers
    getDomainById: (id: string) => Domain | undefined;
    getDomainByName: (name: string) => Domain | undefined;
    getDomainNames: () => string[];
    getActiveDomains: () => Domain[];
    getDomainName: (domainId: string) => string;
    getOutcomeById: (domainId: string, outcomeId: string) => Outcome | undefined;
    getOutcomeName: (domainId: string, outcomeId: string) => string;
    getOutcomeType: (domainId: string, outcomeId: string) => string;
    getOutcomeBetFormat: (domainId: string, outcomeId: string) => BetFormat | undefined;
    getDataPointsMultiplier: (domainId: string, outcomeId: string, operationType: string) => number;
    getOutcomeGroupById: (domainId: string, groupId: string) => OutcomeGroup | undefined;
    getOutcomeGroupName: (domainId: string, groupId: string) => string;
    getOutcomeOrGroupName: (domainId: string, outcomeId: string) => string;
    getBettingStrategyByKey: (key: string) => BettingStrategy | undefined;
    getBettingStrategyName: (key: string) => string;
    reset: () => void;
}

export const useAppStore = create<AppState>((set, get) => ({
    ui: {
        themeMode: (localStorage.getItem('themeMode') as ThemeMode) || 'light',
        sidebar: {
            isCondensed: JSON.parse(localStorage.getItem('SidebarCondensed') || 'false'),
            isMobile: false, // Will be set on initialization
            isOpen: false
        },
        refreshing: false,
        progressMessage: null,
        appRefreshing: false,
        pageRefreshing: false,
        suppressBusMessages: JSON.parse(localStorage.getItem('suppressBusMessages') || 'false'),
        showMemoryMonitor: JSON.parse(localStorage.getItem('showMemoryMonitor') || 'false'),
        showSocketDebug: JSON.parse(localStorage.getItem('showSocketDebug') || 'false'),
        forceEnableModelActions: JSON.parse(localStorage.getItem('forceEnableModelActions') || 'false'),
        settingsDialog: {
            open: false,
            initialTab: 0
        }
    },
    referenceData: {
        domains: [],
        bettingStrategies: [],
        isLoading: false
    },

    setIsCondensed: (value: boolean) => {
        localStorage.setItem('SidebarCondensed', JSON.stringify(value));
        set((state) => ({
            ui: {
                ...state.ui,
                sidebar: {
                    ...state.ui.sidebar,
                    isCondensed: value
                }
            }
        }));
    },

    setIsOpen: (value: boolean) => {
        set((state) => ({
            ui: {
                ...state.ui,
                sidebar: {
                    ...state.ui.sidebar,
                    isOpen: value
                }
            }
        }));
    },

    setRefreshing: (value: boolean) => {
        set((state) => ({
            ui: {
                ...state.ui,
                refreshing: value,
                ...(!value && { progressMessage: null })
            }
        }));
    },

    setProgressMessage: (message: string | null) => {
        set((state) => ({
            ui: {
                ...state.ui,
                progressMessage: message
            }
        }));
    },

    setAppRefreshing: (value: boolean) => {
        set((state) => ({
            ui: {
                ...state.ui,
                appRefreshing: value
            }
        }));
    },

    setPageRefreshing: (value: boolean) => {
        set((state) => ({
            ui: {
                ...state.ui,
                pageRefreshing: value
            }
        }));
    },

    setIsMobile: (value: boolean) => {
        set((state) => ({
            ui: {
                ...state.ui,
                sidebar: {
                    ...state.ui.sidebar,
                    isMobile: value
                }
            }
        }));
    },

    setSuppressBusMessages: (value: boolean) => {
        localStorage.setItem('suppressBusMessages', JSON.stringify(value));
        set((state) => ({
            ui: {
                ...state.ui,
                suppressBusMessages: value
            }
        }));
    },

    setShowMemoryMonitor: (value: boolean) => {
        localStorage.setItem('showMemoryMonitor', JSON.stringify(value));
        set((state) => ({
            ui: {
                ...state.ui,
                showMemoryMonitor: value
            }
        }));
    },

    setShowSocketDebug: (value: boolean) => {
        localStorage.setItem('showSocketDebug', JSON.stringify(value));
        set((state) => ({
            ui: {
                ...state.ui,
                showSocketDebug: value
            }
        }));
    },

    setForceEnableModelActions: (value: boolean) => {
        localStorage.setItem('forceEnableModelActions', JSON.stringify(value));
        set((state) => ({
            ui: {
                ...state.ui,
                forceEnableModelActions: value
            }
        }));
    },

    setThemeMode: (mode: ThemeMode) => {
        localStorage.setItem('themeMode', mode);
        set((state) => ({
            ui: {
                ...state.ui,
                themeMode: mode
            }
        }));
    },

    openSettingsDialog: (tab = 0) => {
        set((state) => ({
            ui: {
                ...state.ui,
                settingsDialog: {
                    open: true,
                    initialTab: tab
                }
            }
        }));
    },

    closeSettingsDialog: () => {
        set((state) => ({
            ui: {
                ...state.ui,
                settingsDialog: {
                    open: false,
                    initialTab: 0
                }
            }
        }));
    },

    loadReferenceData: async (api) => {
        const user = useUserStore.getState().user;
        if (!user) return;

        try {
            set((state) => ({
                referenceData: { ...state.referenceData, isLoading: true }
            }));

            const [domainsResponse, bettingStrategiesResponse] = await Promise.all([
                api.domain.getDomains('sports', true),
                api.model.getBettingStrategies(),
            ]);

            set((state) => ({
                referenceData: {
                    ...state.referenceData,
                    domains: domainsResponse,
                    bettingStrategies: bettingStrategiesResponse,
                    isLoading: false
                }
            }));
        } catch (error) {
            console.error('Failed to load reference data:', error);
            set((state) => ({
                referenceData: { ...state.referenceData, isLoading: false }
            }));
        }
    },

    refreshReferenceData: async (api?: ApiServices) => {
        if (api) {
            await get().loadReferenceData(api);
        }
    },

    getDomainById: (id: string) => {
        return get().referenceData.domains.find(domain => domain.id === id);
    },

    getDomainByName: (name: string) => {
        return get().referenceData.domains.find(domain => domain.name === name);
    },

    getDomainNames: () => {
        return get().referenceData.domains.map(domain => domain.name);
    },

    getActiveDomains: () => {
        return get().referenceData.domains.filter(domain => domain.status === 'active');
    },

    getDomainName: (domainId: string) => {
        const domain = get().getDomainById(domainId);
        return domain?.name || domainId;
    },

    getOutcomeById: (domainId: string, outcomeId: string) => {
        const domain = get().getDomainById(domainId);
        return domain?.outcomes.find(o => o.id === outcomeId);
    },

    getOutcomeName: (domainId: string, outcomeId: string) => {
        const domain = get().getDomainById(domainId);
        const outcome = domain?.outcomes.find(o => o.id === outcomeId);
        return outcome?.name || outcomeId;
    },

    getOutcomeType: (domainId: string, outcomeId: string) => {
        const domain = get().getDomainById(domainId);
        const outcome = domain?.outcomes.find(o => o.id === outcomeId);
        return outcome?.type || '';
    },

    getOutcomeBetFormat: (domainId: string, outcomeId: string) => {
        const domain = get().getDomainById(domainId);
        const outcome = domain?.outcomes.find(o => o.id === outcomeId);
        return outcome?.betFormat;
    },

    getDataPointsMultiplier: (domainId: string, outcomeId: string, operationType: string) => {
        // Get the bet format for the outcome (with fallback to inference)
        const outcome = get().getOutcomeById(domainId, outcomeId);
        const betFormat = getBetFormatWithFallback(outcome?.betFormat, outcomeId);

        // Calculate multiplier using centralized helper
        return getMultiplierFromBetFormat(betFormat, operationType);
    },

    getOutcomeGroupById: (domainId: string, groupId: string) => {
        const domain = get().getDomainById(domainId);
        return domain?.outcomeGroups?.find(g => g.id === groupId);
    },

    getOutcomeGroupName: (domainId: string, groupId: string) => {
        const group = get().getOutcomeGroupById(domainId, groupId);
        return group?.name || groupId;
    },

    getOutcomeOrGroupName: (domainId: string, outcomeId: string) => {
        const outcome = get().getOutcomeById(domainId, outcomeId);
        if (!outcome) return outcomeId;

        // If outcome has a groupId, return the group name
        if (outcome.groupId) {
            const groupName = get().getOutcomeGroupName(domainId, outcome.groupId);
            // Only return group name if we found a valid one (not just the groupId back)
            if (groupName !== outcome.groupId) {
                return groupName;
            }
        }

        // Otherwise return the outcome name
        return outcome.name;
    },

    getBettingStrategyByKey: (key: string) => {
        return get().referenceData.bettingStrategies.find(s => s.key === key);
    },

    getBettingStrategyName: (key: string) => {
        const strategy = get().getBettingStrategyByKey(key);
        return strategy?.name || key;
    },

    reset: () => {
        set({ referenceData: { domains: [], bettingStrategies: [], isLoading: false } });
    }
}));

// Initialize window resize listener
export const initializeAppStore = (theme: Theme) => {
    const { setIsMobile } = useAppStore.getState();
    
    // Set initial mobile state
    setIsMobile(window.innerWidth < theme.breakpoints.values.md);
    
    // Handle window resize
    const handleResize = () => {
        setIsMobile(window.innerWidth < theme.breakpoints.values.md);
    };

    window.addEventListener('resize', handleResize);
    
    return () => {
        window.removeEventListener('resize', handleResize);
    };
}; 